/**
 * Terminal UI Zustand store.
 *
 * Holds the rendered history of inputs/results and the user's
 * command history (for up/down arrow recall). The engine itself
 * is stateless from the UI's perspective — this store is the
 * single source of truth for what the terminal panel shows.
 */

import { create } from "zustand";
import { vybeTraderTerminalDslEngine } from "./engine/VybeTraderTerminalDslEngine";
import { formatTerminalValue } from "./format";
import type {
  TerminalEntry,
  TerminalStore,
  TerminalStoreState,
} from "./types";

const MAX_ENTRIES = 500;
const MAX_HISTORY = 200;

let entryCounter = 0;
function nextEntryId(): string {
  entryCounter += 1;
  return `t_${Date.now().toString(36)}_${entryCounter}`;
}

const initialState: TerminalStoreState = {
  entries: [],
  commandHistory: [],
  inputDraft: "",
  explorerDocked: false,
};

export const useTerminalStore = create<TerminalStore>((set, get) => ({
  ...initialState,

  runCommand: async (rawInput: string) => {
    const input = rawInput.trim();
    if (!input) return;

    const id = nextEntryId();
    const pending: TerminalEntry = {
      id,
      createdAt: Date.now(),
      input,
      status: "pending",
    };

    set((state) => {
      const entries = [...state.entries, pending].slice(-MAX_ENTRIES);
      const history = state.commandHistory.filter((h) => h !== input);
      history.push(input);
      while (history.length > MAX_HISTORY) history.shift();
      return { entries, commandHistory: history };
    });

    const result = await vybeTraderTerminalDslEngine.execute(input);

    // Format (and truncate) once at completion so the store never retains
    // raw result values and renders never re-stringify.
    const completion: Partial<TerminalEntry> =
      result.status === "ok"
        ? {
            status: "ok",
            output: formatTerminalValue(result.value),
            durationMs: result.durationMs,
          }
        : {
            status: "error",
            error: result.error,
            durationMs: result.durationMs,
          };

    set((state) => {
      if (state.entries.some((entry) => entry.id === id)) {
        return {
          entries: state.entries.map((entry) =>
            entry.id === id ? { ...entry, ...completion } : entry
          ),
        };
      }
      // The pending entry was pruned/cleared while the command ran —
      // append a fresh completed entry instead of dropping the result.
      const revived: TerminalEntry = { ...pending, ...completion, status: completion.status! };
      return { entries: [...state.entries, revived].slice(-MAX_ENTRIES) };
    });
  },

  clear: () => {
    set((state) => ({ entries: [], commandHistory: state.commandHistory }));
  },

  setInputDraft: (value: string) => {
    set({ inputDraft: value });
  },

  setExplorerDocked: (value: boolean) => {
    set({ explorerDocked: value });
  },
}));
